import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import { Row, Col, Input, Space, Alert, Typography, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Form, StyledModal, CustomNotification } from 'common/components';
import {
  SearchGrid,
  SearchGridItemButton,
  SectionContainer,
} from '../components';
import MemberMapping from './MemberMapping';

import {
  useGetAllMemberForHierarchies,
  useActionMemberHirarchies,
} from './queries';
import { useGetChildrenCompany } from 'pages/company-profile/components/tabs/overview/children-company/hooks';

import { useDispatchReloadPage } from 'hooks/useReloadPage';

import './styles.less';
import { isObject } from 'lodash';

const defaultPageInfo = {
  pageIndex: 1,
  pageSize: 10000,
};

const ParentChildMemberHierarchiesModal = (props, ref) => {
  const {
    searchType = 'member',
    params,
    savedSelected = false,
    service,
    mapSearch,
    itemIdName,
    onlyFetchWithSearchText = true,
    originMember,
    ...rest
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [parentMember, setParentMember] = useState();
  const [childMember, setChildMember] = useState();

  const [showSearchHint, setShowSearchHint] = useState(true);

  const refSearchText = useRef();

  const getAllMemberForHierarchies = useGetAllMemberForHierarchies();
  const actionMemberHirarchies = useActionMemberHirarchies();

  const [formInstance] = Form.useForm();

  const reloadPage = useDispatchReloadPage();

  const { invalidateChildrenCompany } = useGetChildrenCompany({
    enabled: false,
  });

  const fetchItemList = async (customParmas = {}) => {
    //* search Hint only show before first fetch
    showSearchHint && onlyFetchWithSearchText && setShowSearchHint(false);

    getAllMemberForHierarchies.mutate(
      { ...params, ...customParmas },
      {
        onSuccess: (res) => {
          if (res?.isSuccess) setItems(res?.data?.gridData);
        },
      }
    );
  };

  const handleSearchItem = () => {
    const searchTextValue = refSearchText.current;

    if (!searchTextValue) return;

    const params = { searchText: searchTextValue, ...defaultPageInfo };

    fetchItemList(params);
  };

  const handleClearSearch = () => {
    formInstance.resetFields();
    fetchItemList(params);
  };

  const handleSelectItem = (itemData) => {
    setChildMember(itemData);
  };

  const resetAll = () => {
    setChildMember();
    formInstance.resetFields();
    setItems([]);
    setShowSearchHint(true);
  };

  const onChangeSearchText = (e) => {
    refSearchText.current = e.target.value;
  };

  const onRemoveChildMember = () => {
    setChildMember();
  };

  const onSwapMember = () => {
    const tempMember = childMember;
    setChildMember(parentMember);
    setParentMember(tempMember);
  };

  const makeGridItemConfig = (item) => {
    if (parentMember?.id === item?.id) {
      return {
        style: {
          opacity: '0.5',
          pointerEvent: 'none',
        },
        disableClick: true,
      };
    } else return {};
  };

  useImperativeHandle(ref, () => ({
    closeModal: () => {
      setIsModalOpen(false);
    },
    openModal: () => {
      refSearchText.current = null;
      setIsModalOpen(true);
    },
  }));

  useEffect(() => {
    if (isModalOpen) {
      if (isObject(originMember?.hierarchy?.parentMember)) {
        setParentMember({
          ...originMember.hierarchy.parentMember,
          id: originMember.hierarchy.parentMember?.memberId,
        });
        setChildMember(originMember);
      } else {
        setParentMember(originMember);
      }

      !onlyFetchWithSearchText && fetchItemList(params);
    } else {
      !savedSelected && resetAll();
    }
  }, [isModalOpen, savedSelected, onlyFetchWithSearchText]);

  const handleAdd = () => {
    const params = {
      parentId: parentMember.id,
      childId: childMember.id,
      isRemoval: false,
    };
    actionMemberHirarchies.mutate(params, {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          CustomNotification.success(
            'Add parent - child hierarchies successfully'
          );
          reloadPage();
          invalidateChildrenCompany();
          setIsModalOpen(false);
        } else {
          CustomNotification.error(
            res?.message || 'Add parent - child hierarchies error'
          );
        }
      },
      onError: (error) => {
        CustomNotification.error('Add parent - child hierarchies error');
      },
    });
  };

  const handleDelete = () => {
    const params = {
      parentId: parentMember.id,
      childId: childMember.id,
      isRemoval: true,
    };
    actionMemberHirarchies.mutate(params, {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          CustomNotification.success(
            'Delete parent - child hierarchies successfully'
          );
          reloadPage();
          invalidateChildrenCompany();
          setIsModalOpen(false);
        } else {
          CustomNotification.error(
            res?.message || 'Delete parent - child hierarchies error'
          );
        }
      },
      onError: (error) => {
        CustomNotification.error('Delete parent - child hierarchies error');
      },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <StyledModal
        visible={isModalOpen}
        className='parent-child-member-hierarchies__modal'
        title='Parent - Child Hierarchies'
        onCancel={() => {
          setIsModalOpen(false);
        }}
        width={1150}
        centered
        destroyOnClose
        maskClosable={false}
        footer={
          <Space>
            <Button
              type='primary'
              loading={actionMemberHirarchies.isLoading}
              disabled={!childMember}
              onClick={handleAdd}
            >
              Add
            </Button>
            <Button
              type='primary'
              danger
              loading={actionMemberHirarchies.isLoading}
              disabled={!childMember}
              onClick={handleDelete}
            >
              Delete
            </Button>
            <Button
              loading={actionMemberHirarchies.isLoading}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Space>
        }
        {...rest}
      >
        <Row>
          <Col>
            <Input
              style={{ width: 270 }}
              onPressEnter={handleSearchItem}
              allowClear
              onChange={onChangeSearchText}
            />
          </Col>
          <Col>
            <Space>
              <SearchGridItemButton type='primary' onClick={handleSearchItem}>
                <SearchOutlined />
              </SearchGridItemButton>

              {!onlyFetchWithSearchText && (
                <SearchGridItemButton
                  type='primary'
                  onClick={handleClearSearch}
                >
                  Reset
                </SearchGridItemButton>
              )}
            </Space>
          </Col>
        </Row>

        <Row
          style={{
            flex: 1,
            marginLeft: -5,
            marginRight: -5,
            overflow: 'hidden',
          }}
        >
          <Col
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <SectionContainer>
              <SearchGrid
                items={items}
                handleSelectItem={handleSelectItem}
                selectedItems={[childMember]}
                isLoading={getAllMemberForHierarchies.isLoading}
                searchType={searchType}
                itemIdName={itemIdName}
                showSearchHint={showSearchHint}
                makeGridItemConfig={makeGridItemConfig}
              />
            </SectionContainer>
          </Col>
          <Col style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Row style={{ flex: 1, alignItems: 'flex-start' }}>
              <Col xs={24}>
                <SectionContainer>
                  <Alert
                    message='Informational Note'
                    description={
                      <Typography.Paragraph>
                        Please note that Parent - Child links for a member
                        company are for visibility of the parent company to all
                        entities owned by child companies. Children do not have
                        visibility to entities owned by the parent or other
                        children unless they are expressly shared with that
                        child company.
                      </Typography.Paragraph>
                    }
                    type='info'
                    showIcon
                    style={{ height: '100%', paddingTop: 8 }}
                  />
                </SectionContainer>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <SectionContainer>
                  <MemberMapping
                    originMember={parentMember}
                    childMember={childMember}
                    onRemoveChildMember={onRemoveChildMember}
                    onSwapMember={onSwapMember}
                  />
                </SectionContainer>
              </Col>
            </Row>
          </Col>
        </Row>
      </StyledModal>
    </>
  );
};

export default forwardRef(ParentChildMemberHierarchiesModal);
